/**
 * Pit to Port, the body of the section at the top of a cargo delivery's HOW
 * tab (Chris's walk-through, go-live 1 November 2026). IMT's copy of the HMS
 * section, in IMT's own form boxes and colours; DeliveryPitToPortSection
 * gives it its card and buttons (renderButton).
 *
 * Loading locations (mines) and delivery locations: each a CRM partner and
 * one of its saved addresses, with tonnes. A partner not in CRM yet is made a
 * draft partner on the spot (the delivery's "Create CRM partner" pop-up, on a
 * draft delivery). A partner with no saved address, or an operator who can't
 * open CRM, types it here; it is saved onto the partner with the delivery.
 * The rules live in ./pitToPort.js; the server checks them again on save.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import addressFromSuggestion from '../../utils/addressFromSuggestion';
import AddressInput from '../ui/AddressInput';
import Field from '../ui/Field';
import AmountInput from '../ui/AmountInput';
import { QUANTITY_DECIMALS } from '../../utils/precision';
import DeliveryPartnerSearch from '../DeliveryPartnerSearch';
import {
  DRAFT_PARTNER_TYPE,
  KINDS,
  KIND_WORDS,
  TYPE_NEW,
  blankStop,
  isTyping,
  routeLine,
  sideTally,
  sideUnitWords,
  stopsOf,
  withAddressChoice,
  withPartner,
  withSide,
} from './pitToPort';
import './PitToPort.css';

const SAVED_COPY = '__saved_copy__';

// IMT's delivery form boxes: a label tied to its control.
function Box({ id, label, children }) {
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

// A partner's saved addresses, through the delivery door (operators without
// CRM access read them here).
// Only the latest request's answer counts (a slow earlier one, even for the
// same partner, is dropped), and the previous partner's addresses clear the
// moment the partner changes. A refusal keeps the server's sentence.
function usePartnerSites(crmId) {
  const [state, setState] = useState({ crmId: null, sites: [], loaded: false, failed: false, refusal: '' });
  const latestRequest = useRef(0);
  const load = useCallback(async () => {
    latestRequest.current += 1;
    const request = latestRequest.current;
    setState({ crmId, sites: [], loaded: false, failed: false, refusal: '' });
    if (!crmId) return;
    try {
      const response = await axiosInstance.get('/api/deliveries/partner-sites/', { params: { crm_id: crmId } });
      if (latestRequest.current !== request) return;
      setState({ crmId, sites: response.data?.sites || [], loaded: true, failed: false, refusal: '' });
    } catch (error) {
      if (latestRequest.current !== request) return;
      console.error('Pit to Port: could not load the partner addresses', error);
      const refusal = error.response?.status === 403 ? (error.response.data?.error || '') : '';
      setState({ crmId, sites: [], loaded: false, failed: true, refusal });
    }
  }, [crmId]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

function Tally({ formData, kind, unitWords }) {
  const tally = sideTally(formData, kind);
  const words = KIND_WORDS[kind];
  const unit = unitWords ? ` ${unitWords}` : '';
  let text = null;
  let tone = 'info';
  if (tally.state === 'missing') {
    text = `${tally.label} needs its tonnes: with two or more ${words.many.toLowerCase()}, each has tonnes, adding up to the delivery's ${words.total}.`;
    tone = 'warn';
  } else if (tally.state === 'no-total') {
    text = `${tally.added}${unit} so far. The delivery has no ${words.total} yet to add up to.`;
  } else if (tally.state === 'ok') {
    text = `${tally.added} of ${tally.total}${unit}: adds up to the delivery's ${words.total}.`;
    tone = 'ok';
  } else if (tally.state === 'off') {
    text = tally.over
      ? `${tally.added}${unit} is more than the delivery's ${words.total} of ${tally.total}${unit}.`
      : `${tally.added} of ${tally.total}${unit}: ${tally.left}${unit} still to place.`;
    tone = 'warn';
  }
  if (!text) return null;
  return <p className={`ptp-tally ptp-tally-${tone}`} data-testid={`ptp-tally-${kind}`}>{text}</p>;
}

function Stop({
  stop, index, kind, unitWords, onChange, onRemove, createPartner, pendingCreatedPartners, renderButton,
}) {
  const { sites, loaded, failed, refusal, reload } = usePartnerSites(stop.crm_id);
  const typing = isTyping(stop, sites, loaded);
  const title = `${KIND_WORDS[kind].one} ${index + 1}`;
  const savedCopy = !stop.address_id && !typing && stop.address;

  let choice = '';
  if (stop.address_id) choice = String(stop.address_id);
  else if (typing) choice = TYPE_NEW;
  else if (savedCopy) choice = SAVED_COPY;
  // A picked address still shows while its partner's list loads.
  const pickedMissing = stop.address_id && !sites.some((s) => String(s.id) === String(stop.address_id));

  return (
    <div className="ptp-stop" data-testid={`ptp-stop-${kind}-${index}`}>
      <div className="ptp-stop-head">
        <span className="ptp-stop-title">{title}</span>
        {stop.partner_is_draft && (
          <span className="ptp-draft" title="Added on a delivery; waiting for CRM approval">Draft partner</span>
        )}
        <span className="ptp-stop-remove">
          {renderButton({ action: 'remove', label: `Remove ${title.toLowerCase()}`, onClick: onRemove })}
        </span>
      </div>
      <div className="ptp-stop-grid">
        <div className="ptp-partner">
          <DeliveryPartnerSearch
            label="CRM partner"
            fieldName={`ptp-${stop.key}-partner`}
            fieldNameId={`ptp-${stop.key}-partner-id`}
            value={stop.partner_name || ''}
            onChange={(_field, _fieldId, partnerId, partnerName) => {
              const isDraft = (pendingCreatedPartners || []).some((p) => p.id === partnerId);
              // The pick lands after the partner's record loads: build on the
              // stop as it is then, so tonnes typed meanwhile stay.
              onChange((current) => withPartner(current, partnerId, partnerName, isDraft));
            }}
            onCreatePartner={createPartner}
            extraVisibleProfiles={pendingCreatedPartners}
          />
        </div>

        <div className="ptp-address">
          {stop.crm_id ? (
            <>
              <Box id={`ptp-${stop.key}-address`} label="Address">
                <select
                  id={`ptp-${stop.key}-address`}
                  className="form-input"
                  value={choice}
                  onChange={(e) => {
                    if (e.target.value === SAVED_COPY) return;
                    const picked = e.target.value;
                  onChange((current) => withAddressChoice(current, picked, sites));
                  }}
                  data-testid={`ptp-address-${kind}-${index}`}
                >
                  <option value="">{failed ? 'Choose' : (loaded ? 'Pick one of its addresses' : 'Loading its addresses…')}</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>{site.address}</option>
                  ))}
                  {pickedMissing && <option value={stop.address_id}>{stop.address}</option>}
                  {savedCopy && <option value={SAVED_COPY}>{`${stop.address} (as saved on this delivery)`}</option>}
                  <option value={TYPE_NEW}>Type a new address…</option>
                </select>
              </Box>
              {failed && refusal && (
                <p className="ptp-note ptp-note-warn">{refusal} Type the address instead.</p>
              )}
              {failed && !refusal && (
                <p className="ptp-note ptp-note-warn">
                  Couldn&apos;t load this partner&apos;s addresses.{' '}
                  <button type="button" className="ptp-link" onClick={reload}>Try again</button>
                  {' '}or type the address.
                </p>
              )}
              {typing && (
                <Field
                  label="New address"
                  hint={`Saved onto ${stop.partner_name || 'the partner'} in CRM when you save the delivery.`}
                >
                  <AddressInput
                    name={`ptp-${stop.key}-new-address`}
                    value={stop.new_address || ''}
                    onChange={(e) => {
                      const words = e.target.value;
                      onChange((current) => ({
                        ...current,
                        address_id: null,
                        typing: true,
                        new_address: words,
                        // A pick's map point stays while its words do.
                        new_address_data: current.new_address_data?.formatted_address === words
                          ? current.new_address_data : null,
                      }));
                    }}
                    onSelect={(suggestion) => {
                      const data = addressFromSuggestion(suggestion);
                      onChange((current) => ({
                        ...current, address_id: null, typing: true, new_address: data.formatted_address || '', new_address_data: data,
                      }));
                    }}
                    placeholder="Start typing the site's address…"
                    styleType="crm"
                    rows={2}
                  />
                </Field>
              )}
            </>
          ) : (
            <Field label="Address" hint="Pick the CRM partner first.">
              <span className="ptp-address-empty">No partner picked</span>
            </Field>
          )}
        </div>

        <div className="ptp-quantity">
          <Box id={`ptp-${stop.key}-tonnes`} label={unitWords ? `Tonnes (${unitWords})` : 'Tonnes'}>
            <AmountInput
              id={`ptp-${stop.key}-tonnes`}
              className="form-input"
              value={stop.quantity ?? ''}
              maxDecimals={QUANTITY_DECIMALS}
              onChange={(figure) => onChange((current) => ({ ...current, quantity: figure }))}
              data-testid={`ptp-quantity-${kind}-${index}`}
            />
          </Box>
        </div>
      </div>
    </div>
  );
}

function RouteLine({ formData }) {
  const steps = routeLine(formData);
  if (steps.length === 0) return null;
  return (
    <ol className="ptp-route" aria-label="Route" data-testid="ptp-route">
      {steps.map((step, i) => (
        <React.Fragment key={`${step.kind}-${i}`}>
          {i > 0 && <li className="ptp-route-arrow" aria-hidden="true">{'\u2192'}</li>}
          <li className={`ptp-route-step ptp-route-${step.kind}`}>
            <span className="ptp-route-name">{step.name}</span>
            {step.place && <span className="ptp-route-place">{step.place}</span>}
          </li>
        </React.Fragment>
      ))}
    </ol>
  );
}

export default function PitToPortLocations({
  formData, handleInputChange, units, inlineCrmCreate, pendingCreatedPartners, renderButton,
}) {
  // Every row write starts from the latest rows, never the rows as last drawn:
  // the draft-partner pop-up and the partner search answer later, and two
  // writes in one click must build on each other (writeSide updates this).
  const latest = useRef(formData);
  latest.current = formData;

  const writeSide = (kind, stops) => {
    const next = withSide(latest.current, kind, stops);
    // Two writes in one click (a pick, then its text) must build on each
    // other, not both on the stops as they were before the click.
    latest.current = { ...latest.current, route_locations: next };
    handleInputChange({ target: { name: 'route_locations', value: next } });
  };
  // `change` is the new stop, or a function of the stop as it is now.
  const updateStop = (kind, key, change) => {
    writeSide(kind, stopsOf(latest.current, kind).map((s) => {
      if (s.key !== key) return s;
      return typeof change === 'function' ? change(s) : change;
    }));
  };

  return (
    <div className="ptp-body">
      <p className="ptp-intro">
        Where the cargo is loaded inland (mines) and delivered after the port. Each is a CRM partner and one of
        its addresses. Leave this empty for a port-to-port delivery; for an inland-only delivery, leave the vessel
        and port boxes empty.
      </p>
      <RouteLine formData={formData} />
      {KINDS.map((kind) => {
        const stops = stopsOf(formData, kind);
        const unitWords = sideUnitWords(formData, kind, units);
        return (
          <div className="ptp-side" key={kind} data-testid={`ptp-side-${kind}`}>
            <h3 className="ptp-side-title">{KIND_WORDS[kind].many}</h3>
            {stops.map((stop, index) => {
              const opener = inlineCrmCreate ? inlineCrmCreate({
                initialPartnerType: DRAFT_PARTNER_TYPE[kind],
                title: `Create CRM partner for ${KIND_WORDS[kind].one.toLowerCase()} ${index + 1}`,
                onCreated: (profile) => {
                  const current = stopsOf(latest.current, kind).find((s) => s.key === stop.key);
                  if (current) updateStop(kind, stop.key, withPartner(current, profile.id, profile.legal_entity_name, true));
                },
              }) : undefined;
              return (
                <Stop
                  key={stop.key}
                  stop={stop}
                  index={index}
                  kind={kind}
                  unitWords={unitWords}
                  onChange={(next) => updateStop(kind, stop.key, next)}
                  onRemove={() => writeSide(kind, stopsOf(latest.current, kind).filter((s) => s.key !== stop.key))}
                  createPartner={opener}
                  pendingCreatedPartners={pendingCreatedPartners}
                  renderButton={renderButton}
                />
              );
            })}
            <Tally formData={formData} kind={kind} unitWords={unitWords} />
            <div className="ptp-add">
              {renderButton({
                action: 'add',
                label: KIND_WORDS[kind].add,
                onClick: () => writeSide(kind, [...stopsOf(latest.current, kind), blankStop(kind)]),
                testId: `ptp-add-${kind}`,
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
